import { Button } from '@/components/ui/primitives';
import { CtaBand, ContextNote, Dateline, FAQList, FeatureGrid, Hero, MarketingPage, PageFooter, StepList } from '@/components/marketing';
import { MEASURE_HREFLANG_ALTERNATES } from '@/pages/landingContent';
import { pageForRoute } from '@/site/pageRegistry';

const PAGE = pageForRoute('/fr/mesurer-pdf');

const STEPS = [
    {
        title: 'Calibrer l’échelle du plan',
        body: 'Choisissez deux points sur une longueur connue, saisissez sa longueur réelle et appliquez l’échelle. Par exemple, à l’échelle 1:50, 1 cm sur le plan représente 50 cm en réalité.',
        image: '/tool-shots/dimension.jpg',
        alt: 'Cote calibrée sur un plan de pièce',
    },
    {
        title: 'Mesurer une distance',
        body: 'Choisissez l’outil de cotation et marquez les deux extrémités. Vérifiez la mesure à l’aide d’une autre longueur connue.',
        image: '/tool-shots/dimension.jpg',
        alt: 'Ligne de cotation et mesure sur un plan',
    },
    {
        title: 'Délimiter une pièce pour sa surface et son périmètre',
        body: 'Tracez un polygone ou un rectangle autour de la pièce. Ces deux formes calculent la surface en m² et le périmètre en m ; une ellipse calcule la surface.',
        image: '/tool-shots/polygon.jpg',
        alt: 'Polygone qui délimite une pièce sur un plan',
    },
    {
        title: 'Exporter les mesures en CSV',
        body: 'Exportez la liste des annotations en CSV pour retrouver les distances, surfaces et périmètres calculés.',
        image: '/tool-shots/rectangle.jpg',
        alt: 'Rectangle et annotations de mesure sur un plan PDF',
    },
] as const;

const FAQ_ITEMS = [
    { question: 'Le PDF doit-il contenir une échelle ?', answer: ['Non. Calibrez l’échelle en choisissant deux points sur une longueur connue et en saisissant sa longueur réelle. Vérifiez ensuite la mesure avec une deuxième longueur connue.'] },
    { question: 'Puis-je mesurer des distances et des pièces ?', answer: ['Oui. L’outil de cotation mesure les distances. Les polygones et les rectangles calculent la surface et le périmètre ; les ellipses calculent la surface.'] },
    { question: 'Quelles unités sont disponibles ?', answer: ['Vous pouvez utiliser le système métrique ou impérial. Les valeurs figurent dans la liste des annotations et dans l’export CSV.'] },
    { question: 'Mon PDF est-il envoyé en ligne ?', answer: ['Non. Le PDF est ouvert dans le navigateur, sur votre appareil. Aucun compte n’est nécessaire.'] },
] as const;

export function FrMesurerPdfPage() {
    return (
        <MarketingPage head={{ ...PAGE.head, canonical: PAGE.canonical, lang: 'fr', alternates: MEASURE_HREFLANG_ALTERNATES }}>
            <main>
                <Hero
                    eyebrow="Mesurer un PDF"
                    title={<>Mesurer un plan PDF :<br />distances, surfaces et périmètres.</>}
                    lead="Calibrez d’abord l’échelle du plan. Mesurez ensuite des distances et délimitez les pièces pour calculer leur surface et leur périmètre."
                    cta={<Button href="/" variant="primary" size="lg">Ouvrir un PDF et mesurer</Button>}
                    chips={['Unités métriques', 'Échelles 1:50 et 1:100', 'Export CSV']}
                />
                <ContextNote text="L'interface de l'outil est en anglais. Votre PDF est ouvert localement dans le navigateur et n’est pas envoyé en ligne." />
                <StepList title="Comment mesurer un plan PDF" lead="De l’échelle calibrée à l’export CSV : quatre étapes pour vos mesures." steps={STEPS} />
                <FeatureGrid
                    sectionTitle="Des mesures pour vos plans"
                    sectionLead="Calibrez le plan puis choisissez la forme adaptée à la mesure."
                    cards={[
                        { title: 'Distances', body: 'L’outil de cotation affiche la longueur réelle entre deux points.' },
                        { title: 'Surfaces et périmètres', body: 'Les polygones et les rectangles calculent la surface et le périmètre. Les ellipses calculent la surface.' },
                        { title: 'Exporter les mesures', body: 'La liste des annotations contient les valeurs calculées et peut être exportée en CSV.' },
                    ]}
                />
                <FAQList items={FAQ_ITEMS} />
                <CtaBand
                    title="Ouvrez un PDF et commencez à mesurer"
                    lead="Calibrez une longueur connue, puis mesurez distances et pièces."
                    actions={<><Button href="/" variant="primary" size="lg">Ouvrir un PDF</Button><Button href="/fr/" variant="ghost" size="lg">Découvrir les outils</Button></>}
                />
                <Dateline reviewed={PAGE.reviewed} locale="fr" />
            </main>
            <PageFooter tagline="Mesurer des plans PDF dans le navigateur" />
        </MarketingPage>
    );
}
